import React from "react";
import { Link } from "react-router-dom";
import { 
  ChevronRight, 
  Home, 
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Phone,
  Search,
  Heart,
  Brain,
  Users,
  Shield,
  Activity,
  Clock
} from "lucide-react";
import { Header, Footer } from "../../../components/Layout";

const AlcoholPage = () => {
  const symptoms = [
    { title: "Tolerancia aumentada", description: "Necesitas beber más para sentir el mismo efecto que antes" },
    { title: "Síntomas de abstinencia", description: "Temblores, sudoración, ansiedad o náuseas al dejar de beber" },
    { title: "Pérdida de control", description: "Bebes más de lo que planeas o no puedes detenerte" },
    { title: "Abandono de actividades", description: "Dejas de hacer cosas que antes disfrutabas por beber" },
    { title: "Problemas laborales/familiares", description: "El alcohol afecta tus relaciones y trabajo" },
    { title: "Beber a pesar de consecuencias", description: "Continúas bebiendo aunque cause problemas de salud o legales" }
  ];

  const healthEffects = [
    { organ: "Hígado", effect: "Hepatitis alcohólica, cirrosis, cáncer", severity: "alto" },
    { organ: "Cerebro", effect: "Daño cognitivo, demencia, síndrome de Wernicke-Korsakoff", severity: "alto" },
    { organ: "Corazón", effect: "Cardiomiopatía, arritmias, hipertensión", severity: "medio" },
    { organ: "Páncreas", effect: "Pancreatitis aguda y crónica", severity: "alto" },
    { organ: "Sistema inmune", effect: "Mayor susceptibilidad a infecciones", severity: "medio" },
    { organ: "Salud mental", effect: "Depresión, ansiedad, trastornos del sueño", severity: "alto" }
  ];

  const treatmentOptions = [
    {
      title: "Desintoxicación Médica",
      description: "Primera etapa crucial. El síndrome de abstinencia alcohólica puede ser peligroso y requiere supervisión médica.",
      duration: "7-14 días",
      icon: Activity
    },
    {
      title: "Tratamiento Residencial",
      description: "Internación completa para casos severos. Incluye desintoxicación, terapia intensiva y comunidad terapéutica.",
      duration: "6-12 meses + seguimiento",
      icon: Shield
    },
    {
      title: "Tratamiento Ambulatorio",
      description: "Terapia regular sin internación. Ideal para personas con adicción moderada y buen sistema de apoyo. Desde $350.000/mes.",
      duration: "6-12 meses + seguimiento",
      icon: Clock
    },
    {
      title: "Grupos de Apoyo (AA)",
      description: "Alcohólicos Anónimos ofrece apoyo gratuito y continuo. Complemento esencial del tratamiento profesional.",
      duration: "Continuo",
      icon: Users
    }
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalCondition",
    "name": "Alcoholismo",
    "alternateName": ["Trastorno por consumo de alcohol", "Adicción al alcohol"],
    "description": "El alcoholismo es una enfermedad crónica caracterizada por la dependencia física y psicológica al alcohol.",
    "signOrSymptom": symptoms.map(s => ({
      "@type": "MedicalSignOrSymptom",
      "name": s.title
    })),
    "possibleTreatment": treatmentOptions.map(t => ({
      "@type": "MedicalTherapy",
      "name": t.title
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
      { "@type": "ListItem", "position": 2, "name": "Adicciones", "item": "https://sinadicciones.org/adicciones" },
      { "@type": "ListItem", "position": 3, "name": "Alcoholismo", "item": "https://sinadicciones.org/adicciones/alcohol" }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Alcoholismo: Síntomas, Efectos y Tratamiento en Chile | Sin Adicciones</title>
        <meta name="description" content="Guía completa sobre el alcoholismo en Chile. Conoce los síntomas, efectos en la salud, opciones de tratamiento y cómo buscar ayuda profesional." />
        <meta property="og:title" content="Alcoholismo: Síntomas, Efectos y Tratamiento" />
        <meta property="og:description" content="Todo sobre el alcoholismo: síntomas de alerta, consecuencias para la salud y opciones de tratamiento disponibles en Chile." />
        <link rel="canonical" href="https://sinadicciones.org/adicciones/alcohol" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/adicciones/alcohol" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      

      <Header />
      <div className="h-16 md:h-20" />

      {/* Breadcrumb */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Inicio
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/adicciones" className="hover:text-emerald-400 transition-colors">
              Adicciones
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-300">Alcoholismo</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/20 via-slate-900 to-slate-950" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-20">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-5xl">🍺</span>
              <div className="px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full">
                <span className="text-amber-400 font-medium">Adicción más común en Chile</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Alcoholismo:{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">
                Síntomas y Tratamiento
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl">
              El alcoholismo afecta a más del 10% de la población adulta en Chile. 
              Es una enfermedad tratable. Conoce las señales de alerta y las opciones de recuperación.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/herramientas/audit"
                className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
              >
                <Activity className="w-5 h-5" />
                Test de Evaluación (AUDIT)
              </Link>
              <Link 
                to="/centros-de-rehabilitacion-drogas-chile"
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                Buscar Tratamiento
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Symptoms Section */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Señales de Alerta
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Reconocer el problema es el primer paso hacia la recuperación. ¿Te identificas con alguna de estas señales?
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {symptoms.map((symptom, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-800/50 rounded-2xl border border-amber-500/20 hover:border-amber-500/40 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">{symptom.title}</h3>
                    <p className="text-slate-400 text-sm">{symptom.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-slate-400 mb-4">¿Reconoces 3 o más señales?</p>
            <Link 
              to="/herramientas/audit"
              className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-all"
            >
              Hacer Test de Evaluación
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Health Effects */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Efectos en la Salud
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              El consumo excesivo de alcohol daña múltiples órganos y sistemas del cuerpo.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {healthEffects.map((effect, index) => (
              <div 
                key={index}
                className={`p-6 rounded-2xl border ${
                  effect.severity === 'alto' 
                    ? 'bg-red-500/10 border-red-500/30' 
                    : 'bg-yellow-500/10 border-yellow-500/30'
                }`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Brain className={`w-5 h-5 ${effect.severity === 'alto' ? 'text-red-400' : 'text-yellow-400'}`} />
                  <h3 className="text-lg font-bold text-white">{effect.organ}</h3>
                  <span className={`ml-auto text-xs px-2 py-1 rounded-full ${
                    effect.severity === 'alto' 
                      ? 'bg-red-500/20 text-red-400' 
                      : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    Riesgo {effect.severity}
                  </span>
                </div>
                <p className="text-slate-300 text-sm">{effect.effect}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Treatment Options */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Opciones de Tratamiento
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              El alcoholismo es una enfermedad tratable. Estas son las opciones más efectivas según la evidencia clínica.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {treatmentOptions.map((option, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700/50 hover:border-emerald-500/30 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <option.icon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-bold text-white">{option.title}</h3>
                      <span className="text-emerald-400 text-sm">{option.duration}</span>
                    </div>
                    <p className="text-slate-400">{option.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link 
              to="/centros-de-rehabilitacion-drogas-chile"
              className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
            >
              <Search className="w-5 h-5" />
              Buscar Centros
            </Link>
            <Link 
              to="/grupos-aa"
              className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
            >
              <Users className="w-5 h-5" />
              Encontrar Grupo AA
            </Link>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-amber-900/30 to-slate-800/50 rounded-3xl p-8 md:p-12 border border-amber-500/20">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 text-center">
              Alcoholismo en Chile
            </h2>
            
            <div className="grid sm:grid-cols-3 gap-8 text-center">
              <div>
                <p className="text-4xl font-bold text-amber-400 mb-2">10.6%</p>
                <p className="text-slate-400">de adultos con consumo problemático</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-amber-400 mb-2">3°</p>
                <p className="text-slate-400">causa de muerte evitable</p>
              </div>
              <div>
                <p className="text-4xl font-bold text-amber-400 mb-2">70%</p>
                <p className="text-slate-400">recuperación con tratamiento adecuado</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-900/50 to-slate-800/50 border border-emerald-500/20 p-8 md:p-12 text-center">
            <Heart className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              La Recuperación es Posible
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Miles de personas han superado el alcoholismo con el tratamiento adecuado. 
              El primer paso es pedir ayuda.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/asesoria"
                className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-emerald-500/25 flex items-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Asesoría Experta
              </Link>
              <a 
                href="tel:1412"
                className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                Fono Drogas: 1412
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Related Pages */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white mb-6">Contenido Relacionado</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "Cocaína y Pasta Base", path: "/adicciones/cocaina", icon: "⚡" },
              { title: "Tratamiento Residencial", path: "/centro/residencial", icon: "🏥" },
              { title: "Grupos de Apoyo (AA)", path: "/grupos-aa", icon: "👥" },
              { title: "Tratamientos Gratuitos", path: "/centros-rehabilitacion-gratis", icon: "💚" }
            ].map((item, index) => (
              <Link 
                key={index}
                to={item.path}
                className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-emerald-500/30 transition-all group"
              >
                <span className="text-2xl">{item.icon}</span>
                <span className="text-white group-hover:text-emerald-400 transition-colors">{item.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AlcoholPage;
